
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Clock, User, MapPin } from 'lucide-react';

const DealershipSignoffPage = ({ dealership, masterTemplate, onUpdateDealership, dropdownOptions }) => {
    const [signOffMode, setSignOffMode] = useState('individual'); // 'individual', 'group', or 'custom'
    const [selectedMessages, setSelectedMessages] = useState({ sales: {}, service: {} });
    const [signOffPerson, setSignOffPerson] = useState({ sales: {}, service: {} });
    const [selectedLocation, setSelectedLocation] = useState({ sales: '', service: '' });
    const [selectedLocations, setSelectedLocations] = useState({ sales: {}, service: {} });
    const [dealerLocations, setDealerLocations] = useState([]);
    const [fieldConfig, setFieldConfig] = useState({
        sales: [
            { id: 'email', name: 'Email Address', type: 'email', required: true },
            { id: 'name', name: 'Name/Job Title', type: 'text', required: true },
            { id: 'signedOff', name: 'Signed Off', type: 'checkbox', required: false }
        ],
        service: [
            { id: 'email', name: 'Email Address', type: 'email', required: true },
            { id: 'name', name: 'Name/Job Title', type: 'text', required: true },
            { id: 'signedOff', name: 'Signed Off', type: 'checkbox', required: false }
        ]
    });

    // Fetch dealer locations on component mount
    useEffect(() => {
        fetchDealerLocations();
    }, []);

    const fetchDealerLocations = async () => {
        try {
            const response = await fetch('/api/dealer-locations-with-addresses');
            if (response.ok) {
                const locations = await response.json();
                setDealerLocations(locations);
            }
        } catch (error) {
            console.error('Error fetching dealer locations:', error);
        }
    };

    // Get available message types from dropdown options
    const getMessageTypes = (type) => {
        return dropdownOptions?.[`${type}MessageNames`] || [];
    };

    // Get actual locations for this dealership only
    const getLocations = () => {
        // Filter locations by current dealership's group_id
        const filteredLocations = dealerLocations.filter(loc => loc.group_id === dealership.id);

        return filteredLocations.length > 0
            ? filteredLocations.map(loc => loc.name || `Location ${loc.id}`)
            : ['Main Location'];
    };

    // Get sign-off status for a message/location combination
    const getSignOffStatus = (type, messageType, location) => {
        const key = `${type}-${messageType}-${location}`;
        return dealership.signoffStatus?.[key] || null;
    };

    // Handle message selection for sign-off
    const handleMessageSelection = (type, messageType, isSelected) => {
        setSelectedMessages(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [messageType]: isSelected
            }
        }));
    };

    // Handle sign-off form field changes
    const handleSignOffFieldChange = (type, fieldId, value) => {
        setSignOffPerson(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [fieldId]: value
            }
        }));
    };

    // Handle location selection for individual mode
    const handleLocationChange = (type, location) => {
        setSelectedLocation(prev => ({
            ...prev,
            [type]: location
        }));
    };

    // Handle multiple location selection for custom mode
    const handleCustomLocationChange = (type, location, isSelected) => {
        setSelectedLocations(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [location]: isSelected
            }
        }));
    };

    // Handle sign-off submission
    const handleSignOff = (type) => {
        const selectedMsgs = Object.keys(selectedMessages[type] || {}).filter(
            msg => selectedMessages[type][msg]
        );

        if (selectedMsgs.length === 0) {
            alert('Please select at least one message to sign off.');
            return;
        }

        // Validate required fields
        const requiredFields = fieldConfig[type].filter(field => field.required);
        const personData = signOffPerson[type] || {};
        const missingFields = requiredFields.filter(field =>
            !personData[field.id] ||
            (field.type === 'checkbox' && !personData[field.id])
        );

        // Validate location selection based on mode
        if (signOffMode === 'individual' && !selectedLocation[type]) {
            alert('Please select a location for individual sign-off.');
            return;
        }

        if (signOffMode === 'custom') {
            const customSelectedCount = Object.values(selectedLocations[type] || {}).filter(Boolean).length;
            if (customSelectedCount === 0) {
                alert('Please select at least one location for custom sign-off.');
                return;
            }
        }

        if (missingFields.length > 0) {
            alert(`Please fill in required fields: ${missingFields.map(f => f.name).join(', ')}`);
            return;
        }

        // Create sign-off data
        const signOffData = { ...personData, signedOff: true, signedAt: new Date().toISOString() };
        let locations;

        if (signOffMode === 'group') {
            locations = getLocations();
        } else if (signOffMode === 'individual') {
            locations = [selectedLocation[type]];
        } else if (signOffMode === 'custom') {
            locations = Object.keys(selectedLocations[type] || {}).filter(loc => selectedLocations[type][loc]);
        }

        // Update sign-off status for selected messages
        const newSignoffStatus = { ...dealership.signoffStatus };
        selectedMsgs.forEach(messageType => {
            locations.forEach(location => {
                const key = `${type}-${messageType}-${location}`;
                newSignoffStatus[key] = signOffData;
            });
        });

        onUpdateDealership({
            ...dealership,
            signoffStatus: newSignoffStatus
        });

        // Reset form
        setSelectedMessages(prev => ({ ...prev, [type]: {} }));
        setSignOffPerson(prev => ({ ...prev, [type]: {} }));
        if (signOffMode === 'individual') {
            setSelectedLocation(prev => ({ ...prev, [type]: '' }));
        } else if (signOffMode === 'custom') {
            setSelectedLocations(prev => ({ ...prev, [type]: {} }));
        }
    };

    // Render message section with sign-off status
    const renderMessageSection = (type) => {
        const messageTypes = getMessageTypes(type);
        const locations = getLocations();
        const selectedCount = Object.values(selectedMessages[type] || {}).filter(Boolean).length;
        const personData = signOffPerson[type] || {};

        return (
            <div className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold capitalize">{type} Messages</h3>
                    <div className="text-sm text-gray-600">
                        {selectedCount} of {messageTypes.length} selected
                    </div>
                </div>

                {/* Sign-off Form - NOW AT THE TOP */}
                {selectedCount > 0 && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold mb-4 flex items-center gap-2 text-blue-800">
                            <User size={16} />
                            Sign-off Person ({type})
                            <span className="text-sm font-normal text-blue-600">
                                - {selectedCount} message{selectedCount > 1 ? 's' : ''} selected
                            </span>
                        </h4>

                        {/* Location Selector for Individual Mode */}
                        {signOffMode === 'individual' && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-blue-800">
                                    Select Location *
                                </label>
                                <select
                                    value={selectedLocation[type] || ''}
                                    onChange={e => handleLocationChange(type, e.target.value)}
                                    className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white"
                                    required
                                >
                                    <option value="">Choose a location...</option>
                                    {locations.map(location => (
                                        <option key={location} value={location}>{location}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-blue-600 mt-1">
                                    Individual mode: Sign off this specific location only
                                </p>
                            </div>
                        )}

                        {/* Location Multi-Selector for Custom Mode */}
                        {signOffMode === 'custom' && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-blue-800">
                                    Select Locations *
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 border border-blue-300 rounded-lg bg-white">
                                    {locations.map(location => {
                                        const isSelected = selectedLocations[type]?.[location] || false;
                                        return (
                                            <label key={location} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={e => handleCustomLocationChange(type, location, e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 rounded"
                                                />
                                                <span className="text-sm">{location}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-blue-600 mt-1">
                                    Custom mode: Sign off selected locations only ({Object.values(selectedLocations[type] || {}).filter(Boolean).length} selected)
                                </p>
                            </div>
                        )}

                        {/* Sign-off Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fieldConfig[type].map(field => (
                                <div key={field.id}>
                                    <label className="block text-sm font-medium mb-1 text-blue-800">
                                        {field.name} {field.required && '*'}
                                    </label>
                                    {field.type === 'checkbox' ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={personData[field.id] || false}
                                                onChange={e => handleSignOffFieldChange(type, field.id, e.target.checked)}
                                                className="w-4 h-4 text-blue-600 rounded"
                                            />
                                            <span className="text-sm text-blue-700">
                                                {field.name}
                                            </span>
                                        </div>
                                    ) : (
                                        <input
                                            type={field.type}
                                            value={personData[field.id] || ''}
                                            onChange={e => handleSignOffFieldChange(type, field.id, e.target.value)}
                                            className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm"
                                            placeholder={`Enter ${field.name.toLowerCase()}`}
                                            required={field.required}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-4">
                            <button
                                onClick={() => handleSignOff(type)}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 font-medium"
                            >
                                <Check size={16} />
                                Sign Off Selected {type} Messages
                                {signOffMode === 'individual' && selectedLocation[type] && (
                                    <span className="text-sm opacity-90">({selectedLocation[type]})</span>
                                )}
                                {signOffMode === 'custom' && (
                                    <span className="text-sm opacity-90">
                                        ({Object.values(selectedLocations[type] || {}).filter(Boolean).length} location{Object.values(selectedLocations[type] || {}).filter(Boolean).length !== 1 ? 's' : ''})
                                    </span>
                                )}
                                {signOffMode === 'group' && (
                                    <span className="text-sm opacity-90">(All Locations)</span>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                <div className="space-y-3">
                    {messageTypes.map(messageType => (
                        <div key={messageType} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-center gap-4">
                                <input
                                    type="checkbox"
                                    id={`${type}-${messageType}`}
                                    checked={selectedMessages[type]?.[messageType] || false}
                                    onChange={e => handleMessageSelection(type, messageType, e.target.checked)}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <label
                                    htmlFor={`${type}-${messageType}`}
                                    className="flex-1 font-medium cursor-pointer"
                                >
                                    {messageType}
                                </label>

                                {/* Location Status Icons */}
                                <div className="flex items-center gap-2">
                                    {locations.map(location => {
                                        const status = getSignOffStatus(type, messageType, location);
                                        return (
                                            <div key={location} className="flex items-center gap-1 text-xs">
                                                <MapPin size={12} className="text-gray-400" />
                                                <span className="text-gray-600">{location}</span>
                                                {status ? (
                                                    <div className="flex items-center gap-1 text-green-600">
                                                        <Check size={14} />
                                                        <span title={`Signed by ${status.name || status.email}`}>
                                                            ✓
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <Clock size={14} className="text-orange-500" title="Pending" />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}

                    {messageTypes.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            No {type} message types configured
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="mb-6">
                <Link to={`/setup/${dealership.id}`} className="text-sm text-blue-600 hover:underline">
                    &larr; Back to {dealership.name}
                </Link>
            </div>

            <div className="mb-6">
                <h2 className="text-3xl font-bold mb-2">{dealership.name} - Message Sign Off</h2>
                <p className="text-gray-600">
                    Sign off on message types for your locations. Select messages and provide sign-off details.
                </p>
            </div>

            {/* Mode Toggle */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Sign-off Mode</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="signOffMode"
                            value="individual"
                            checked={signOffMode === 'individual'}
                            onChange={e => setSignOffMode(e.target.value)}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span>Individual Location</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="signOffMode"
                            value="custom"
                            checked={signOffMode === 'custom'}
                            onChange={e => setSignOffMode(e.target.value)}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span>Custom Selection</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="signOffMode"
                            value="group"
                            checked={signOffMode === 'group'}
                            onChange={e => setSignOffMode(e.target.value)}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span>All Locations</span>
                    </label>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                    {signOffMode === 'group' && 'Signs off all locations for selected messages'}
                    {signOffMode === 'individual' && 'Signs off one specific location'}
                    {signOffMode === 'custom' && 'Signs off multiple selected locations'}
                </p>
            </div>

            {/* Message Sections */}
            <div className="space-y-8">
                {renderMessageSection('sales')}
                {renderMessageSection('service')}
            </div>
        </div>
    );
};

export default DealershipSignoffPage;
